import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Query } from "@apollo/client/react/components";
import { graphql } from "@apollo/client/react/hoc";
import compose from "lodash.flowright";
import { Row, Col } from "react-flexbox-grid";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { ElementsConsumer } from "@stripe/react-stripe-js";

import { fonts } from "../../../styles/variables";
import breakpoints from "../../../styles/breakpoints";
import { GET_AUTH, GET_TOP_UP_INFO } from "../../../graphql/queries";
import { logError } from "../../../helpers/logging";
import {
  DB_ID_ORDER_CATEGORY_ONE_OFF_WINES,
  FETCH_POLICY_CACHE_ONLY,
  SETTINGS_TRAY,
  TOP_UP_VALUES,
  ORDER_CATEGORY_ID_TO_NAME,
  I18N_NAMESPACES,
  PAYMENT_INTENT_STATUS_SUCCEEDED,
  FACEBOOK_TRACKING_CURRENCY,
} from "../../../helpers/constants";
import RadioGroup from "../../atoms/RadioGroup";
import Image from "../../atoms/Image";
import StripeElements from "../../molecules/StripeElements";
import { showErrorAlert, showInfoAlertWithComponent } from "../../../helpers/alerts";
import urlPatterns from "../../../urls";
import { formatNumber, shouldUseCheckoutV2 } from "../../../helpers/tools";
import {
  ONE_CLICK_TOP_UP,
  REQUEST_WINE_STOCK,
  CREATE_ONE_CLICK_TOP_UP_PAYMENT_INTENT,
} from "../../../graphql/mutations";

import { LabelTan } from "../../atoms/Label";
import { AlignCenter } from "../../atoms/Aligned";
import Loading from "../../atoms/Loading";
import OffCanvasTray from "../OffCanvasTray";
import OffCanvasTrayHeader from "../../molecules/OffCanvasTrayHeader";
import OffCanvasTrayClose from "../../atoms/OffCanvasTrayClose";
import OffCanvasTrayHeaderTitle from "../../atoms/OffCanvasTrayHeaderTitle";
import OffCanvasTrayBody from "../../molecules/OffCanvasTrayBody";
import Title from "../../atoms/Title";
import CellarRadio from "../../atoms/CellarRadio";
import OffCanvasTrayFooter from "../../molecules/OffCanvasTrayFooter";
import Button from "../../atoms/Button";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import {
  StyledCheckoutTotalItemPriceBold,
  StyledCheckoutTotal,
  StyledCheckoutTotalItem,
  StyledCheckoutTotalItemLeft,
  StyledCheckoutTotalItemRight,
} from "../../atoms/CheckoutTotalList";
import AnimatingTrayContainer from "../../atoms/AnimatingTrayContainer";
import CustomAlert from "../../atoms/CustomAlert";
import { getCashAmountLabel } from "../../../helpers/i18n";
import { CellarBottleHolder } from "../../atoms/CellarBottleHolder";

const TrayCellarBottleHolder = styled(CellarBottleHolder)`
  background-color: ${(props) => props.baseColor};
  margin: -20px -15px 20px -15px;
  ${breakpoints.smUp} {
    display: none;
  }
`;

const CellarTrayRadioGroup = styled(RadioGroup)`
  margin-bottom: 20px;
`;

const StyledSubmitBtnPrice = styled.span`
  font-size: 25px;
  font-family: ${fonts.fontInterstateBlackCompressed};
`;

const RotatedTopupBottleImg = styled(Image)`
  max-height: 300px;
  transform: rotate(90deg);
  display: block;
  margin: 0 auto;
`;

const CheckoutTotalItemPriceBoldWithMinWidth = styled(StyledCheckoutTotalItemPriceBold)`
  min-width: 40px;
  display: inline-block;
`;

const StyledOffCanvasTrayFooter = styled(OffCanvasTrayFooter)`
  padding-top: 0px;
  ${breakpoints.smDown} {
    padding-top: 0px;
  }
`;

const TopUpTrayCheckoutTotal = styled(StyledCheckoutTotal)`
  margin-top: 0px;
`;

const StyledAlignCenter = styled(AlignCenter)`
  padding-top: 5px;
`;

class TopUpTrayContent extends Component {
  static propTypes = {
    isOffCanvasTopUpOpened: PropTypes.bool.isRequired,
    wine: PropTypes.shape({}).isRequired,
    baseColor: PropTypes.string.isRequired,
    bottleImage: PropTypes.string.isRequired,
    handleOffCanvasTopUpChange: PropTypes.func.isRequired,
    oneClickTopup: PropTypes.func.isRequired,
    createOneClickTopupPaymentIntent: PropTypes.func.isRequired,
    history: PropTypes.shape({}).isRequired,
    authQuery: PropTypes.shape({}).isRequired,
    requestWineStock: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    isInBoxAndCartTray: PropTypes.bool,
  };

  state = {
    topUpSelectedValue: null,
    topUpWineQuantity: TOP_UP_VALUES[0],
    isTopupInProgress: false,
    hide: false,
  };

  handleQuantityPick = (event) => {
    event.preventDefault();
    const value = parseInt(event.target.value, 10);
    const quantity = TOP_UP_VALUES[value - 1];
    this.setState({
      topUpWineQuantity: quantity,
      topUpSelectedValue: value.toString(),
    });
  };

  /**
   * Toggles open/close of the "Add bottle" panel.
   */
  handleOneClickSubmitChange = () => {
    this.setState((prevState) => ({
      isTopupInProgress: !prevState.isTopupInProgress,
    }));
  };

  /**
   * Charges the one click topup using the appropriate method.
   *
   * @param {Number} memberId
   * @param {Number} wineId
   * @param {Number} quantity
   * @returns {Object}
   */
  chargeOneClickTopup = async (memberId, wineId, quantity) => {
    const { createOneClickTopupPaymentIntent, stripe, oneClickTopup, t } = this.props;

    if (shouldUseCheckoutV2()) {
      const {
        data: {
          createOneClickTopupPaymentIntent: { errors, clientSecret, defaultPaymentMethodApiUuid },
        },
      } = await createOneClickTopupPaymentIntent({
        variables: {
          input: {
            wineId,
            quantity,
          },
        },
      });

      if (errors || !clientSecret || !defaultPaymentMethodApiUuid) {
        return { errors, isSuccessful: false };
      }

      const paymentResult = await stripe.confirmCardPayment(clientSecret, {
        payment_method: defaultPaymentMethodApiUuid,
      });

      if (!paymentResult || paymentResult.error) {
        const errorMessage = t(`stripe:errors.${paymentResult.error.code}`, paymentResult.error.message);
        return {
          isSuccessful: false,
          errors: [{ messages: [errorMessage] }],
        };
      }

      return {
        isSuccessful: paymentResult.paymentIntent.status === PAYMENT_INTENT_STATUS_SUCCEEDED,
        errors: null,
      };
    }

    // PLA-250: Remove this legacy method once switch to PaymentIntentAPI is complete
    const {
      data: { oneClickTopup: oneClickTopupData },
    } = await oneClickTopup({
      variables: {
        input: {
          memberId,
          wineId,
          quantity,
        },
      },
    });

    return oneClickTopupData;
  };

  /**
   * Submits a one-off top-up purchase.
   *
   * @param {event} event
   * @param {Number} netTotal
   * @param {Number} discountValue
   */
  handleOneClickTopup = (event, netTotal, discountValue) => {
    event.preventDefault();
    const { authQuery, wine, history } = this.props;
    const { memberId } = authQuery.auth;
    const { topUpWineQuantity } = this.state;
    this.handleOneClickSubmitChange();
    this.chargeOneClickTopup(memberId, wine.id, topUpWineQuantity)
      .then(({ errors, isSuccessful, latestOrderId }) => {
        if (isSuccessful) {
          history.push(urlPatterns.CHECKOUT_THANK_YOU, {
            checkout: {
              // Used by 3rd parties (e.g. Facebook pixel)
              order_id: latestOrderId,
              currency: FACEBOOK_TRACKING_CURRENCY,
              value: formatNumber(netTotal),
              revenue: formatNumber(netTotal),
              content_category: "checkout",
              order_category_id: DB_ID_ORDER_CATEGORY_ONE_OFF_WINES,
              order_category_name: ORDER_CATEGORY_ID_TO_NAME[DB_ID_ORDER_CATEGORY_ONE_OFF_WINES],
              order_discount: discountValue,
            },
            isGift: false,
            isSubscription: false,
          });
        } else if (errors) {
          const errorMessages = errors[0].messages;
          showErrorAlert(errorMessages[0] || "Sorry, something went wrong. Please try again.");
          this.handleOneClickSubmitChange();
        } else {
          showErrorAlert("Sorry, the payment was declined.");
          this.handleOneClickSubmitChange();
        }
      })
      .catch((err) => {
        logError(err, { location: "TopUpTray", action: "CREATE_ONE_CLICK_TOP_UP_PAYMENT_INTENT" });
        showErrorAlert("Sorry, something went wrong. Please try again.");
        this.handleOneClickSubmitChange();
      });
  };

  requestWineStock = (e) => {
    e.preventDefault();
    const { requestWineStock, authQuery, wine, history } = this.props;
    const { memberId } = authQuery.auth;
    requestWineStock({
      variables: {
        input: {
          memberId,
          wineId: wine.id,
        },
      },
    })
      .then(({ data }) => {
        if (data.requestWineStock && data.requestWineStock.errors) {
          logError(data.requestWineStock.errors[0].messages);
          showErrorAlert("Sorry, something went wrong. Please try again.");
        }
        const successMsg = "Thank you! We'll try our best to get it back in stock.";
        const clickableMsg = "REVIEW COMMUNICATION SETTINGS";
        const alertComponent = (
          <CustomAlert
            message={successMsg}
            clickableMsg={clickableMsg}
            onClick={() => {
              history.push(urlPatterns.MY_ACCOUNT_TRAY(SETTINGS_TRAY.COMMUNICATIONS));
            }}
            clickableMsgInNewLine
            isClickableUppercase
          />
        );
        showInfoAlertWithComponent(alertComponent, { autoClose: 5000 });
      })
      .catch(() => {
        logError("Error on request wine stock");
        showErrorAlert("Sorry, something went wrong.");
      });
  };

  /**
   * this function sets the state of hide to true,
   * so, the hiding action can start animating,
   * then, close the tray after 200ms.
   * */
  handleCloseTray = () => {
    this.setState({ hide: true });
    const { handleOffCanvasTopUpChange } = this.props;
    setTimeout(() => handleOffCanvasTopUpChange(), 200);
  };

  render() {
    const { isOffCanvasTopUpOpened, authQuery, wine, baseColor, bottleImage, isInBoxAndCartTray } =
      this.props;
    const { memberId } = authQuery.auth;

    const { topUpSelectedValue, topUpWineQuantity, isTopupInProgress, hide } = this.state;

    if (isOffCanvasTopUpOpened) {
      return (
        <OffCanvasTray
          setOverflowHidden={false}
          isOffcanvasOpened={isOffCanvasTopUpOpened}
          outSideClickHandler={() => this.handleCloseTray()}
          isInBoxAndCartTray={isInBoxAndCartTray}
        >
          <AnimatingTrayContainer isOpened={isOffCanvasTopUpOpened} hide={hide}>
            <OffCanvasTrayHeader>
              <OffCanvasTrayClose onClick={() => this.handleCloseTray()} showBackIcon />
              <OffCanvasTrayHeaderTitle>Top-up</OffCanvasTrayHeaderTitle>
            </OffCanvasTrayHeader>

            <Query
              query={GET_TOP_UP_INFO}
              variables={{
                memberId,
                wineId: wine.id,
                quantity: topUpWineQuantity,
              }}
              partialRefetch
            >
              {({ loading, error, data }) => {
                if (loading) {
                  return <Loading />;
                }
                if (error) logError(error, { location: "TopUpTray", action: "GET_TOP_UP_INFO" });
                const { topupSummary } = data || {};
                const { totalPrice, discountValue, shippingFee, hasStock } = topupSummary || {};
                const netTotal = totalPrice - discountValue + shippingFee;
                return (
                  <>
                    <OffCanvasTrayBody>
                      <Title tag={2} uppercase hiddenSm>
                        Top-up
                      </Title>

                      <TrayCellarBottleHolder baseColor={baseColor}>
                        <RotatedTopupBottleImg src={bottleImage} maxFull />
                      </TrayCellarBottleHolder>

                      <CellarTrayRadioGroup
                        selectedValue={topUpSelectedValue}
                        onChange={this.handleQuantityPick}
                      >
                        <Row>
                          <Col xs>
                            <CellarRadio value="1" label="6" sublabel="bottles" isDefault />
                          </Col>

                          <Col xs>
                            <CellarRadio value="2" label="12" sublabel="bottles" />
                          </Col>
                        </Row>
                      </CellarTrayRadioGroup>

                      <StyledAlignCenter>
                        <p>
                          {hasStock ? (
                            <small>Top-up orders are dispatched immediately</small>
                          ) : (
                            <small>
                              {"Currently we don't have this quantity in stock. Request " +
                                "to get more stock below."}
                            </small>
                          )}
                        </p>
                      </StyledAlignCenter>
                    </OffCanvasTrayBody>

                    <StyledOffCanvasTrayFooter>
                      {hasStock ? (
                        <>
                          <TopUpTrayCheckoutTotal>
                            <StyledCheckoutTotalItem>
                              <StyledCheckoutTotalItemLeft>Wine total</StyledCheckoutTotalItemLeft>

                              <StyledCheckoutTotalItemRight>
                                <StyledCheckoutTotalItemPriceBold>
                                  {getCashAmountLabel(totalPrice)}
                                </StyledCheckoutTotalItemPriceBold>
                              </StyledCheckoutTotalItemRight>
                            </StyledCheckoutTotalItem>

                            <StyledCheckoutTotalItem>
                              <StyledCheckoutTotalItemLeft>Discount</StyledCheckoutTotalItemLeft>

                              <StyledCheckoutTotalItemRight>
                                <LabelTan>10% top-up saving</LabelTan>
                                <CheckoutTotalItemPriceBoldWithMinWidth>
                                  {getCashAmountLabel(discountValue && -discountValue)}
                                </CheckoutTotalItemPriceBoldWithMinWidth>
                              </StyledCheckoutTotalItemRight>
                            </StyledCheckoutTotalItem>

                            <StyledCheckoutTotalItem>
                              <StyledCheckoutTotalItemLeft>Shipping</StyledCheckoutTotalItemLeft>

                              <StyledCheckoutTotalItemRight>
                                <StyledCheckoutTotalItemPriceBold>
                                  {shippingFee === 0 ? "Free" : getCashAmountLabel(shippingFee)}
                                </StyledCheckoutTotalItemPriceBold>
                              </StyledCheckoutTotalItemRight>
                            </StyledCheckoutTotalItem>
                          </TopUpTrayCheckoutTotal>
                          <Button
                            onClick={(event) => this.handleOneClickTopup(event, netTotal, discountValue)}
                            disabled={isTopupInProgress}
                            between
                            full
                          >
                            <span>{isTopupInProgress ? "Processing..." : "Confirm top-up"}</span>
                            <StyledSubmitBtnPrice>{getCashAmountLabel(netTotal)}</StyledSubmitBtnPrice>
                          </Button>
                        </>
                      ) : (
                        <Button onClick={this.requestWineStock} full lg>
                          Request stock
                        </Button>
                      )}
                    </StyledOffCanvasTrayFooter>
                  </>
                );
              }}
            </Query>
          </AnimatingTrayContainer>
        </OffCanvasTray>
      );
    }
    return null;
  }
}

TopUpTrayContent.defaultProps = {
  isInBoxAndCartTray: false,
};

const TopUpTray = (props) => (
  <StripeElements>
    <ElementsConsumer>
      {({ stripe, elements }) => {
        if (!stripe) return <StyledLoading />;

        return <TopUpTrayContent {...props} stripe={stripe} elements={elements} />;
      }}
    </ElementsConsumer>
  </StripeElements>
);

export default compose(
  withRouter,
  graphql(GET_AUTH, {
    name: "authQuery",
    options: {
      fetchPolicy: FETCH_POLICY_CACHE_ONLY,
    },
  }),
  graphql(REQUEST_WINE_STOCK, { name: "requestWineStock" }),
  graphql(CREATE_ONE_CLICK_TOP_UP_PAYMENT_INTENT, {
    name: "createOneClickTopupPaymentIntent",
  }),
  graphql(ONE_CLICK_TOP_UP, { name: "oneClickTopup" }),
  withTranslation([I18N_NAMESPACES.COMMON, I18N_NAMESPACES.STRIPE])
)(TopUpTray);
